import React from 'react';
import { Container, Box, Typography, Grid, Card, Avatar, Chip } from '@mui/material';
import {
  Rocket, Security, Speed, Groups, TrendingUp, Code, Shield
} from '@mui/icons-material';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import { useTranslation } from 'react-i18next';

const AboutPage = () => {
  const { t } = useTranslation();
  const seo = getPageSEO('about');

  const values = [
    { icon: <Security />, title: t('website.about.values.securityFirst.title'), desc: t('website.about.values.securityFirst.desc') },
    { icon: <Speed />, title: t('website.about.values.innovation.title'), desc: t('website.about.values.innovation.desc') },
    { icon: <Groups />, title: t('website.about.values.customerSuccess.title'), desc: t('website.about.values.customerSuccess.desc') },
    { icon: <Shield />, title: t('website.about.values.reliability.title'), desc: t('website.about.values.reliability.desc') }
  ];

  const tech = [
    { name: 'PostgreSQL', desc: 'Robust database', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
    { name: 'Express.js', desc: 'Fast backend', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg' },
    { name: 'React', desc: 'Modern UI', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
    { name: 'Node.js', desc: 'Scalable runtime', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
    { name: 'JWT', desc: 'Secure auth', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/json/json-original.svg' },
    { name: 'AWS', desc: 'Cloud infrastructure', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg' }
  ];

  const timeline = t('website.about.timeline.events', { returnObjects: true }) || [];

  return (
    <WebsiteLayout>
      <SEO {...seo} />
      <HeroSection chip={t('website.about.heroChip')}>
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
            {t('website.about.heroTitle')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, lineHeight: 1.6, color: 'primary.contrastText' }}>
            {t('website.about.heroSubtitle')}
          </Typography>
        </Box>
      </HeroSection>

      {/* Introduction Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 3 }}>
              {t('website.about.intro.title')}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: 18, color: 'text.secondary', lineHeight: 1.8, mb: 3 }}>
              {t('website.about.intro.body1')}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: 18, color: 'text.secondary', lineHeight: 1.8 }}>
              {t('website.about.intro.body2')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop"
              alt="Team collaboration"
              sx={{
                width: '100%',
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Mission & Vision */}
      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white', py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <Rocket sx={{ fontSize: 40, color: 'primary.contrastText' }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 3 }}>
                {t('website.about.mission.title')}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: 18, color: 'text.secondary', lineHeight: 1.8 }}>
                {t('website.about.mission.body')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <TrendingUp sx={{ fontSize: 40, color: 'primary.contrastText' }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 3 }}>
                {t('website.about.vision.title')}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: 18, color: 'text.secondary', lineHeight: 1.8 }}>
                {t('website.about.vision.body')}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why FluxERP */}
      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', py: 12 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2 }}>
              {t('website.about.whyBuilt.title')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 800, mx: 'auto' }}>
              {t('website.about.whyBuilt.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%', border: '1px solid', borderColor: 'grey.200', boxShadow: 'none' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('website.about.whyBuilt.problem.title')}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {t('website.about.whyBuilt.problem.body')}
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%', border: '1px solid', borderColor: 'grey.200', boxShadow: 'none' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('website.about.whyBuilt.solution.title')}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {t('website.about.whyBuilt.solution.body')}
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, height: '100%', border: '1px solid', borderColor: 'grey.200', boxShadow: 'none' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('website.about.whyBuilt.impact.title')}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {t('website.about.whyBuilt.impact.body')}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Core Values */}
      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white', py: 12 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2 }}>
              {t('website.about.values.title')}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      bgcolor: 'secondary.main',
                      color: 'secondary.contrastText'
                    }}
                  >
                    {value.icon}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {value.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Technology Stack */}
      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', py: 12 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Code sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2 }}>
              {t('website.about.tech.title')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {t('website.about.tech.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {tech.map((item, idx) => (
              <Grid item xs={6} sm={4} md={2} key={idx}>
                <Card
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white',
                    boxShadow: 'none',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)'
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={item.icon}
                    alt={item.name}
                    sx={{ width: 48, height: 48, mx: 'auto', mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {item.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Timeline */}
      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white', py: 12 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2 }}>
              {t('website.about.timeline.title')}
            </Typography>
          </Box>

          <Box sx={{ position: 'relative' }}>
            {timeline.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  gap: 4,
                  mb: 6,
                  position: 'relative',
                  '&:before': idx < timeline.length - 1 ? {
                    content: '""',
                    position: 'absolute',
                    left: 31,
                    top: 64,
                    width: 2,
                    height: 'calc(100% + 24px)',
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.300'
                  } : {}
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: 'secondary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'secondary.contrastText',
                    fontWeight: 800,
                    fontSize: 18,
                    flexShrink: 0,
                    zIndex: 1
                  }}
                >
                  {item.year}
                </Box>
                <Box sx={{ flex: 1, pt: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {item.event}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Security Philosophy */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 12,
          mb: 0
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Shield sx={{ fontSize: 64, mb: 3, color: 'primary.contrastText' }} />
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
              {t('website.about.security.title')}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, lineHeight: 1.8, color: 'primary.contrastText' }}>
              {t('website.about.security.body')}
            </Typography>
          </Box>
        </Container>
      </Box>
    </WebsiteLayout>
  );
};

export default AboutPage;
